import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import type { PayslipFilters } from '@/features/payslips/api'
import { PayslipListScreen } from '@/features/payslips/payslip-list-screen'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the payslips list.
 *
 * `payslips.$payslipId.tsx` is a CHILD of this route, so without an Outlet the
 * detail URL silently renders this list.
 *
 * Filters live in URL search params so a filtered view is linkable. The
 * `search` filter narrows by employee name and number client-side until the
 * API carries a search contract.
 *
 * Guarded on `payslip.view`, which is the SCOPE-carrying grant: the API
 * narrows the list by its scope (company-wide or only the caller's own), so
 * this shell only decides whether the screen exists at all.
 */
export const Route = createFileRoute('/_authenticated/payroll/payslips')({
  validateSearch: (search: Record<string, unknown>): PayslipFilters => ({
    search: typeof search.search === 'string' && search.search ? search.search : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'payslip.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: PayrollPayslipsRoute,
})

function PayrollPayslipsRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <PayslipListScreen
      params={params}
      onParamsChange={(next) => void navigate({ to: '/payroll/payslips', search: next as never })}
    />
  )
}
